"use client";

import { Fragment, useMemo } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Search, X } from "lucide-react";
import { SearchBar } from "@/components/search-bar";
import { BrandCard } from "@/components/brand-card";
import { Pagination } from "@/components/pagination";
import { ScrollReveal } from "@/components/scroll-reveal";
import { RecentlyViewed } from "@/components/recently-viewed";
import { parseHeadquarters } from "@/lib/location-utils";
import type { Brand, Category, Company } from "@/lib/types";

type SortOption = "name-asc" | "name-desc" | "updated" | "oldest" | "founded";

interface BrowseContentProps {
  initialBrands: Brand[];
  initialCategories: Category[];
  initialCompanies: Company[];
}

export function BrowseContent({ initialBrands, initialCategories, initialCompanies }: BrowseContentProps) {
  const searchParams = useSearchParams();
  const allBrands = initialBrands;
  const categories = initialCategories;
  const companies = initialCompanies;

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const selectedCategory = searchParams.get("category") || "all";
  const selectedCountry = searchParams.get("country") || "all";
  const selectedCity = searchParams.get("city") || "";
  const selectedState = searchParams.get("state") || "";
  const selectedTag = searchParams.get("tag") || "";
  const searchQuery = searchParams.get("q") || "";
  const sortBy = (searchParams.get("sort") || "name-asc") as SortOption;

  const ITEMS_PER_PAGE = 100;

  // Create a map of company slugs to company objects for quick lookup
  const companyMap = useMemo(() => {
    return companies.reduce((acc, company) => {
      acc[company.slug] = company;
      return acc;
    }, {} as Record<string, Company>);
  }, [companies]);

  // Extract unique countries from all brands for the dropdown
  const uniqueCountries = useMemo(() => {
    if (!allBrands.length) return [];
    const countrySet = new Set<string>();
    for (const brand of allBrands) {
      if (brand.headquarters) {
        const loc = parseHeadquarters(brand.headquarters);
        if (loc.country) countrySet.add(loc.country);
      }
    }
    return Array.from(countrySet).sort();
  }, [allBrands]);

  // Helper to build URL params preserving all current filters
  const buildParams = (overrides: Record<string, string> = {}) => {
    const params = new URLSearchParams();
    const values: Record<string, string> = {
      category: selectedCategory,
      country: selectedCountry,
      city: selectedCity,
      state: selectedState,
      tag: selectedTag,
      sort: sortBy,
      q: searchQuery,
      ...overrides,
    };
    if (values.category && values.category !== "all") params.set("category", values.category);
    if (values.country && values.country !== "all") params.set("country", values.country);
    if (values.city) params.set("city", values.city);
    if (values.state) params.set("state", values.state);
    if (values.tag) params.set("tag", values.tag);
    if (values.sort && values.sort !== "name-asc") params.set("sort", values.sort);
    if (values.q) params.set("q", values.q);
    return params.toString();
  };

  // Filter brands
  let filteredBrands = allBrands.filter(brand => 
    brand?.name && 
    brand?.slug && 
    typeof brand.slug === 'string' && 
    brand.slug.trim() !== '' &&
    brand.name.trim() !== ''
  );

  // Apply search query filter
  if (searchQuery) {
    const lowerQuery = searchQuery.toLowerCase();
    filteredBrands = filteredBrands.filter(brand => 
      brand.name.toLowerCase().includes(lowerQuery) || 
      brand.description.toLowerCase().includes(lowerQuery)
    );
  }

  // Apply category filter
  if (selectedCategory !== "all") {
    filteredBrands = filteredBrands.filter(
      brand => brand.categorySlug === selectedCategory
    );
  }

  // Apply country filter
  if (selectedCountry !== "all") {
    filteredBrands = filteredBrands.filter(brand => {
      if (!brand.headquarters) return false;
      const loc = parseHeadquarters(brand.headquarters);
      return loc.country === selectedCountry;
    });
  }

  // Apply city filter (from URL param, used by home page top cities links)
  if (selectedCity) {
    filteredBrands = filteredBrands.filter(brand => {
      if (!brand.headquarters) return false;
      const loc = parseHeadquarters(brand.headquarters);
      return loc.city === selectedCity;
    });
  }

  // Apply state filter (from URL param, used by home page top states links)
  if (selectedState) {
    filteredBrands = filteredBrands.filter(brand => {
      if (!brand.headquarters) return false;
      const loc = parseHeadquarters(brand.headquarters);
      return loc.state === selectedState;
    });
  }

  // Apply tag filter (from URL param, used by home page top tags links)
  if (selectedTag) {
    filteredBrands = filteredBrands.filter(brand =>
      brand.tags && brand.tags.some(t => t === selectedTag)
    );
  }

  // Apply sorting
  const sortedBrands = [...filteredBrands].sort((a, b) => {
    switch (sortBy) {
      case "name-asc":
        return a.name.localeCompare(b.name);
      case "name-desc":
        return b.name.localeCompare(a.name);
      case "updated":
        return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
      case "oldest":
        return new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime();
      case "founded":
        return (b.foundedYear || 0) - (a.foundedYear || 0);
      default:
        return 0;
    }
  });

  // Calculate pagination
  const totalPages = Math.ceil(sortedBrands.length / ITEMS_PER_PAGE);
  const validPage = Math.min(Math.max(1, currentPage), totalPages || 1);
  const startIndex = (validPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedBrands = sortedBrands.slice(startIndex, endIndex);

  // Build query params for pagination
  const queryParams: Record<string, string> = {};
  if (selectedCategory !== "all") queryParams.category = selectedCategory;
  if (selectedCountry !== "all") queryParams.country = selectedCountry;
  if (selectedCity) queryParams.city = selectedCity;
  if (selectedState) queryParams.state = selectedState;
  if (selectedTag) queryParams.tag = selectedTag;
  if (sortBy !== "name-asc") queryParams.sort = sortBy;
  if (searchQuery) queryParams.q = searchQuery;

  // Determine active filter label for header
  const activeFilterLabel = selectedCity
    ? `Brands in ${selectedCity}`
    : selectedState
    ? `Brands in ${selectedState}`
    : selectedTag
    ? `Brands tagged "${selectedTag}"`
    : searchQuery
    ? `Search Results for "${searchQuery}"`
    : "Browse All Brands";

  return (
    <div className="py-8 md:py-12">
      <RecentlyViewed />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {/* Header */}
            <header className="mb-12">
              <h1 className="text-3xl md:text-4xl font-serif font-semibold text-foreground">
                {activeFilterLabel}
              </h1>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed max-w-3xl">
                {searchQuery 
                  ? `Found ${sortedBrands.length} result${sortedBrands.length !== 1 ? 's' : ''} for your search.`
                  : "Our complete directory of brands and their ownership information. Search for a specific brand or browse our collection to discover who owns the products you use."}
              </p>
            </header>

            {/* Search */}
            <div className="mb-12 max-w-xl">
              <SearchBar 
                placeholder="Search for a brand..." 
                initialData={{
                  brands: allBrands,
                  companies: companies,
                  categories: categories
                }}
                defaultValue={searchQuery}
              />
            </div>

            {/* Filters and Sorting */}
            <div className="mb-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center flex-wrap">
              {/* Category Filter */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-foreground">
                  Filter by Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => {
                    window.location.href = `/browse?${buildParams({ category: e.target.value })}`;
                  }}
                  className="px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm"
                >
                  <option value="all">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.slug} value={cat.slug}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Country Filter */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-foreground">
                  Filter by Country
                </label>
                <select
                  value={selectedCountry}
                  onChange={(e) => {
                    window.location.href = `/browse?${buildParams({ country: e.target.value })}`;
                  }}
                  className="px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm"
                >
                  <option value="all">All Countries</option>
                  {uniqueCountries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-foreground">
                  Sort by
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    window.location.href = `/browse?${buildParams({ sort: e.target.value })}`;
                  }}
                  className="px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm"
                >
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                  <option value="updated">Recently Updated</option>
                  <option value="oldest">Oldest Updated</option>
                  <option value="founded">Founded Year</option>
                </select>
              </div>

              {/* Results count */}
              <div className="text-sm text-muted-foreground sm:ml-auto">
                Showing {paginatedBrands.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, sortedBrands.length)} of {sortedBrands.length} brands
              </div>
            </div>

            {/* Active filter badges */}
            {(selectedCity || selectedState || selectedTag) && (
              <div className="mb-6 flex flex-wrap gap-2">
                {selectedCity && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-accent/10 text-accent text-sm rounded-full">
                    City: {selectedCity}
                    <Link href={`/browse?${buildParams({ city: "" })}`} className="ml-1 hover:text-accent/70">&times;</Link>
                  </span>
                )}
                {selectedState && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-accent/10 text-accent text-sm rounded-full">
                    State: {selectedState}
                    <Link href={`/browse?${buildParams({ state: "" })}`} className="ml-1 hover:text-accent/70">&times;</Link>
                  </span>
                )}
                {selectedTag && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-accent/10 text-accent text-sm rounded-full">
                    Tag: {selectedTag}
                    <Link href={`/browse?${buildParams({ tag: "" })}`} className="ml-1 hover:text-accent/70">&times;</Link>
                  </span>
                )}
              </div>
            )}

            {/* Brand Grid */}
            {paginatedBrands.length > 0 ? (
              <>
                <ScrollReveal className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
                  {paginatedBrands.map((brand) => (
                    <BrandCard
                      key={brand.slug}
                      brand={brand}
                      company={companyMap[brand.companySlug]}
                    />
                  ))}
                </ScrollReveal>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Pagination
                    currentPage={validPage}
                    totalPages={totalPages}
                    baseUrl="/browse"
                    queryParams={queryParams}
                  />
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-muted border border-border mb-6">
                  <Search className="h-8 w-8 text-muted-foreground" />
                </div>
                <h2 className="text-xl font-serif font-semibold text-foreground mb-2">
                  {searchQuery
                    ? `No results for "${searchQuery}"`
                    : "No brands match your filters"}
                </h2>
                <p className="text-muted-foreground max-w-md mx-auto mb-8">
                  {searchQuery
                    ? "We couldn't find any brands matching your search. Try a different keyword or browse our popular categories below."
                    : "Try adjusting or removing some filters to see more results."}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
                  <Link
                    href="/browse"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-colors"
                  >
                    <X className="h-4 w-4" />
                    Clear all filters
                  </Link>
                  <Link
                    href="/categories"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border text-foreground rounded-md hover:bg-muted transition-colors"
                  >
                    Browse all categories
                  </Link>
                </div>

                {/* Suggested categories */}
                <div className="max-w-2xl mx-auto">
                  <p className="text-sm font-medium text-foreground mb-3">Popular categories</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {categories.slice(0, 8).map((cat) => (
                      <Link
                        key={cat.slug}
                        href={`/categories/${cat.slug}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-background border border-border text-foreground hover:border-accent/40 hover:bg-accent/5 hover:text-accent transition-all"
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Suggested searches */}
                {!searchQuery && (
                  <div className="max-w-2xl mx-auto mt-6">
                    <p className="text-sm font-medium text-foreground mb-3">Try searching for</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {["Coca-Cola", "Nike", "Apple", "Tesla", "Pepsi", "Google"].map((term) => (
                        <Link
                          key={term}
                          href={`/browse?q=${encodeURIComponent(term)}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-background border border-border text-muted-foreground hover:border-accent/40 hover:text-accent transition-all"
                        >
                          {term}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Stats Section */}
            <section className="mt-16 p-8 bg-muted rounded-lg border border-border">
              <h2 className="text-xl font-serif font-semibold text-foreground mb-4">
                Database Statistics
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-3xl font-semibold text-accent">
                    {allBrands.length}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Brands</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold text-accent">
                    {categories.length}
                  </p>
                  <p className="text-sm text-muted-foreground">Categories</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold text-accent">
                    {sortedBrands.length}
                  </p>
                  <p className="text-sm text-muted-foreground">Matching Brands</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold text-accent">Daily</p>
                  <p className="text-sm text-muted-foreground">Updates</p>
                </div>
              </div>
            </section>
          </div>

          {/* Sidebar */}
        </div>
      </div>
    </div>
  );
}
